import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import {
  collection,
  query,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  setDoc,
  getDoc
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useAuth } from "./AuthContext";
import {
  Container,
  Table,
  Button,
  Spinner,
  Row,
  Col,
  Nav,
  Modal,
  Form
} from "react-bootstrap";
import Select from 'react-select';
import { uploadBlogImage, getAllBlogs, createBlog, updateBlog, deleteBlog } from "./blogService";
import { getAllNotes, createNote, deleteNote } from "./notesService";
import DataEntryForm from "./dataEntryForm";
import RichTextEditor from "./RichTextEditor";

const AdminPanel = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [subjects, setSubjects] = useState({});
  const [selectedClass, setSelectedClass] = useState("9");
  const [newSubjectKey, setNewSubjectKey] = useState("");
  const [newSubjectLabel, setNewSubjectLabel] = useState("");

  // Blog management state
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const [blogsSaving, setBlogsSaving] = useState(false);
  const [blogForm, setBlogForm] = useState({
    title: "",
    slug: "",
    date: "",
    author: "",
    tag: "",
    excerpt: "",
    content: "",
    status: "draft",
    category: "",
    featured: false,
    imageFile: null,
    image: "",
    seoTitle: "",
    seoDescription: "",
    seoKeywords: "",
    canonicalUrl: "",
    socialTitle: "",
    socialDescription: "",
    socialImage: "",
    featuredImageAlt: "",
    allowComments: true,
  });
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [blogEditItem, setBlogEditItem] = useState(null);
  const [blogChoices, setBlogChoices] = useState([]);

  // Notes management state
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [notesSaving, setNotesSaving] = useState(false);
  const [noteForm, setNoteForm] = useState({ chapterId: '', content: '', seoDescription: '', seoKeywords: '' });
  const [chaptersChoices, setChaptersChoices] = useState([]);

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate("/");
      return;
    }
    fetchAllResources();
    fetchSubjects(selectedClass);
    fetchBlogs();
    fetchBlogChoices();
    fetchChaptersChoices();
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, navigate, selectedClass]);

  const fetchAllResources = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "resources"));
      const snapshot = await getDocs(q);
      const results = [];
      snapshot.forEach((docSnap) => {
        results.push({ ...docSnap.data(), id: docSnap.id });
      });
      setData(results);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChaptersChoices = async () => {
    try {
      const q = query(collection(db, "resources"));
      const snapshot = await getDocs(q);
      const results = [];
      snapshot.forEach((docSnap) => {
        const d = docSnap.data();
        results.push({ id: docSnap.id, title: d.chapterTitle || d.title || docSnap.id });
      });
      setChaptersChoices(results);
    } catch (e) {
      console.error('Error loading chapters list', e);
    }
  };

  const fetchBlogChoices = async () => {
    try {
      // Use all blogs for related-posts selection to avoid missing items due to status mismatches
      const all = await getAllBlogs();
      setBlogChoices(all);
    } catch (e) {
      console.error('Error loading blog choices', e);
    }
  };

  // ---- Blogs CRUD ----
  const fetchBlogs = async () => {
    setBlogsLoading(true);
    try {
      const results = await getAllBlogs();
      setBlogs(results);
    } catch (err) {
      console.error("Error fetching blogs:", err);
    } finally {
      setBlogsLoading(false);
    }
  };

  const fetchNotes = async () => {
    setNotesLoading(true);
    try {
      const all = await getAllNotes();
      setNotes(all);
    } catch (e) {
      console.error('Error fetching notes', e);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleAddBlog = async () => {
    try {
      if (blogsSaving) return;
      setBlogsSaving(true);
      if (!blogForm.slug) {
        alert("Please provide a unique slug for the blog post.");
        setBlogsSaving(false);
        return;
      }
      let imageUrl = blogForm.image || '';
      if (blogForm.imageFile) {
        imageUrl = await uploadBlogImage(blogForm.imageFile);
      }
      const payload = {
        title: blogForm.title,
        slug: blogForm.slug,
        date: blogForm.date || new Date().toISOString().slice(0, 10),
        author: blogForm.author || "CBSE Resources Team",
        tag: blogForm.tag || "General",
        excerpt: blogForm.excerpt,
        content: blogForm.content,
        status: blogForm.status || 'draft',
        category: blogForm.category || 'General',
        featured: !!blogForm.featured,
        image: imageUrl,
        seoTitle: blogForm.seoTitle || blogForm.title,
        seoDescription: blogForm.seoDescription || blogForm.excerpt,
        seoKeywords: blogForm.seoKeywords ? blogForm.seoKeywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        canonicalUrl: blogForm.canonicalUrl || (blogForm.slug ? `${window.location.origin}/blog/${blogForm.slug}` : ''),
        socialTitle: blogForm.socialTitle || blogForm.title,
        socialDescription: blogForm.socialDescription || blogForm.excerpt,
        socialImage: blogForm.socialImage || imageUrl,
        featuredImageAlt: blogForm.featuredImageAlt || blogForm.title,
        allowComments: !!blogForm.allowComments,
        related: Array.isArray(blogForm.related) ? blogForm.related : [],
      };
      await createBlog(payload);
      setBlogForm({
        title: "",
        slug: "",
        date: "",
        author: "",
        tag: "",
        excerpt: "",
        content: "",
        status: "draft",
        category: "",
        featured: false,
        imageFile: null,
        image: "",
        seoTitle: "",
        seoDescription: "",
        seoKeywords: "",
        canonicalUrl: "",
        socialTitle: "",
        socialDescription: "",
        socialImage: "",
        featuredImageAlt: "",
        allowComments: true,
      });
      fetchBlogs();
      alert("Blog post added.");
    } catch (err) {
      console.error("Error adding blog:", err);
      alert("Failed to add blog. Check console for details.");
    }
    finally {
      setBlogsSaving(false);
    }
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await deleteBlog(id);
      setBlogs((prev) => prev.filter((b) => b.id !== id));
      alert("Blog deleted.");
    } catch (err) {
      console.error("Delete blog failed:", err);
    }
  };

  const handleEditBlog = (blog) => {
    setBlogEditItem(blog);
    setShowBlogModal(true);
  };

  const handleSaveBlogEdit = async () => {
    if (!blogEditItem) return;
    try {
      const { id, ...dataToUpdate } = blogEditItem;
      await updateBlog(id, dataToUpdate);
      alert("Blog updated.");
      setShowBlogModal(false);
      fetchBlogs();
    } catch (err) {
      console.error("Update blog failed:", err);
    }
  };

  const fetchSubjects = async (grade) => {
    const ref = doc(db, "subjects", grade);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      setSubjects(snap.data());
    } else {
      setSubjects({});
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this chapter?")) return;
    try {
      await deleteDoc(doc(db, "resources", id));
      setData((prev) => prev.filter((item) => item.id !== id));
      alert("Chapter deleted successfully.");
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const ref = doc(db, "resources", editItem.id);
      const { id, ...dataToUpdate } = editItem;
      await updateDoc(ref, dataToUpdate);
      alert("Chapter updated successfully.");
      setShowModal(false);
      fetchAllResources();
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  const handleAddSubject = async () => {
    const updated = { ...subjects, [newSubjectKey]: newSubjectLabel };
    await setDoc(doc(db, "subjects", selectedClass), updated);
    alert("Subject added.");
    setNewSubjectKey("");
    setNewSubjectLabel("");
    fetchSubjects(selectedClass);
  };

  const handleDeleteSubject = async (key) => {
    const updated = { ...subjects };
    delete updated[key];
    await setDoc(doc(db, "subjects", selectedClass), updated);
    fetchSubjects(selectedClass);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col md={2} className="bg-light vh-100 p-3 border-end">
          <h5>Admin Menu</h5>
          <Nav className="flex-column">
            <Nav.Link onClick={() => setActiveTab("dashboard")} active={activeTab === "dashboard"}>📊 Dashboard</Nav.Link>
            <Nav.Link onClick={() => setActiveTab("add")} active={activeTab === "add"}>➕ Add Data</Nav.Link>
            <Nav.Link onClick={() => setActiveTab("subjects")} active={activeTab === "subjects"}>📚 Manage Subjects</Nav.Link>
            <Nav.Link onClick={() => setActiveTab("blogs")} active={activeTab === "blogs"}>📝 Manage Blogs</Nav.Link>
          <Nav.Link onClick={() => setActiveTab("notes")} active={activeTab === "notes"}>🗒️ Manage Notes</Nav.Link>
            <Nav.Link onClick={handleLogout} className="text-danger mt-3">🔓 Logout</Nav.Link>
          </Nav>
        </Col>

        <Col md={10} className="p-4">
          {activeTab === "dashboard" && (
            <>
              <h4 className="mb-4">📊 Dashboard</h4>
              {loading ? (
                <Spinner animation="border" variant="primary" />
              ) : (
                <Table bordered hover responsive>
                  <thead className="table-dark">
                    <tr>
                      <th>Class</th>
                      <th>Subject</th>
                      <th>Chapter</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, index) => (
                      <tr key={index}>
                        <td>{item.grade}</td>
                        <td>{item.subject}</td>
                        <td>{item.chapterTitle}</td>
                        <td>
                          <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEdit(item)}>Edit</Button>
                          <Button variant="danger" size="sm" onClick={() => handleDelete(item.id)}>Delete</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </>
          )}

          {activeTab === "add" && (
            <>
              <h4 className="mb-4">➕ Add New Resource</h4>
              <DataEntryForm onSuccess={fetchAllResources} />
            </>
          )}

          {activeTab === "subjects" && (
            <>
              <h4 className="mb-4">📚 Manage Subjects</h4>
              <Form.Select className="mb-3" value={selectedClass} onChange={(e) => {
                setSelectedClass(e.target.value);
                fetchSubjects(e.target.value);
              }}>
                {["9", "10", "11", "12"].map((cls) => (
                  <option key={cls} value={cls}>Class {cls}</option>
                ))}
              </Form.Select>

              <Table bordered>
                <thead><tr><th>Code</th><th>Label</th><th>Action</th></tr></thead>
                <tbody>
                  {Object.entries(subjects).map(([key, label]) => (
                    <tr key={key}>
                      <td>{key}</td>
                      <td>{label}</td>
                      <td>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteSubject(key)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Form className="mt-4">
                <Row>
                  <Col md={4}>
                    <Form.Control
                      placeholder="Subject Key (e.g., compSci)"
                      value={newSubjectKey}
                      onChange={(e) => setNewSubjectKey(e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Control
                      placeholder="Subject Label (e.g., Computer Science)"
                      value={newSubjectLabel}
                      onChange={(e) => setNewSubjectLabel(e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <Button onClick={handleAddSubject}>Add Subject</Button>
                  </Col>
                </Row>
              </Form>
            </>
          )}

          {activeTab === "blogs" && (
            <>
              <h4 className="mb-4">📝 Manage Blogs</h4>

              {blogsLoading ? (
                <Spinner animation="border" variant="primary" />
              ) : (
                <Table bordered hover responsive>
                  <thead className="table-dark">
                    <tr>
                      <th>Title</th>
                      <th>Slug</th>
                      <th>Date</th>
                      <th>Author</th>
                      <th>Tag</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs.map((b) => (
                      <tr key={b.id}>
                        <td>{b.title}</td>
                        <td>{b.slug}</td>
                        <td>{b.date}</td>
                        <td>{b.author}</td>
                        <td>{b.tag}</td>
                        <td>
                          <Button size="sm" variant="outline-primary" className="me-2" onClick={() => handleEditBlog(b)}>Edit</Button>
                          <Button size="sm" variant="danger" onClick={() => handleDeleteBlog(b.id)}>Delete</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <h5 className="mt-4">Add New Blog Post</h5>
              <Form>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>Title</Form.Label>
                    <Form.Control value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Slug (unique)</Form.Label>
                    <Form.Control value={blogForm.slug} onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value.trim() })} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Label>Date</Form.Label>
                    <Form.Control placeholder="YYYY-MM-DD" value={blogForm.date} onChange={(e) => setBlogForm({ ...blogForm, date: e.target.value })} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Author</Form.Label>
                    <Form.Control value={blogForm.author} onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })} />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Tag</Form.Label>
                    <Form.Control value={blogForm.tag} onChange={(e) => setBlogForm({ ...blogForm, tag: e.target.value })} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Label>Status</Form.Label>
                    <Form.Select value={blogForm.status || 'draft'} onChange={(e) => setBlogForm({ ...blogForm, status: e.target.value })}>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Category</Form.Label>
                    <Form.Control value={blogForm.category || ''} onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })} />
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <Form.Check
                      type="checkbox"
                      label="Featured"
                      checked={!!blogForm.featured}
                      onChange={(e) => setBlogForm({ ...blogForm, featured: e.target.checked })}
                    />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>Excerpt</Form.Label>
                    <Form.Control as="textarea" rows={2} value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>Content</Form.Label>
                    <RichTextEditor value={blogForm.content} onChange={(html) => setBlogForm({ ...blogForm, content: html })} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>Featured Image</Form.Label>
                    <Form.Control type="file" accept="image/*" onChange={(e) => setBlogForm({ ...blogForm, imageFile: e.target.files?.[0] })} />
                  </Col>
                </Row>
                <hr />
                <h6>SEO</h6>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>SEO Title</Form.Label>
                    <Form.Control value={blogForm.seoTitle} onChange={(e) => setBlogForm({ ...blogForm, seoTitle: e.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Canonical URL</Form.Label>
                    <Form.Control value={blogForm.canonicalUrl} onChange={(e) => setBlogForm({ ...blogForm, canonicalUrl: e.target.value })} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>Meta Description</Form.Label>
                    <Form.Control as="textarea" rows={2} value={blogForm.seoDescription} onChange={(e) => setBlogForm({ ...blogForm, seoDescription: e.target.value })} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>SEO Keywords (comma separated)</Form.Label>
                    <Form.Control value={blogForm.seoKeywords} onChange={(e) => setBlogForm({ ...blogForm, seoKeywords: e.target.value })} />
                  </Col>
                </Row>
                <h6>Social</h6>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>Social Title</Form.Label>
                    <Form.Control value={blogForm.socialTitle} onChange={(e) => setBlogForm({ ...blogForm, socialTitle: e.target.value })} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Social Image URL</Form.Label>
                    <Form.Control value={blogForm.socialImage} onChange={(e) => setBlogForm({ ...blogForm, socialImage: e.target.value })} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>Social Description</Form.Label>
                    <Form.Control as="textarea" rows={2} value={blogForm.socialDescription} onChange={(e) => setBlogForm({ ...blogForm, socialDescription: e.target.value })} />
                  </Col>
                </Row>
                <h6>Advanced</h6>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>Featured Image Alt Text</Form.Label>
                    <Form.Control value={blogForm.featuredImageAlt} onChange={(e) => setBlogForm({ ...blogForm, featuredImageAlt: e.target.value })} />
                  </Col>
                  <Col md={6} className="d-flex align-items-end">
                    <Form.Check
                      type="checkbox"
                      label="Allow Comments"
                      checked={!!blogForm.allowComments}
                      onChange={(e) => setBlogForm({ ...blogForm, allowComments: e.target.checked })}
                    />
                  </Col>
                </Row>
                <h6>Related Posts</h6>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>Select related posts (optional)</Form.Label>
                    <Select
                      isMulti
                      options={blogChoices
                        .filter(b => (b.slug || b.id) !== blogForm.slug)
                        .map(b => ({ value: b.slug || b.id, label: b.title }))}
                      value={(blogForm.related || []).map(v => ({ value: v, label: (blogChoices.find(b => (b.slug || b.id) === v) || {}).title || v }))}
                      onChange={(vals) => setBlogForm({ ...blogForm, related: (vals || []).map(v => v.value) })}
                      classNamePrefix="react-select"
                      placeholder="Search and select related posts"
                    />
                  </Col>
                </Row>
                <Button type="button" onClick={handleAddBlog} disabled={blogsSaving}>
                  {blogsSaving ? 'Adding...' : 'Add Blog'}
                </Button>
              </Form>
            </>
          )}

          {activeTab === "notes" && (
            <>
              <h4 className="mb-4">🗒️ Manage Notes</h4>

              {notesLoading ? (
                <Spinner animation="border" variant="primary" />
              ) : (
                <Table bordered hover responsive>
                  <thead className="table-dark">
                    <tr>
                      <th>Chapter</th>
                      <th>Preview</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notes.map((n) => (
                      <tr key={n.id}>
                        <td>{chaptersChoices.find(c => c.id === n.chapterId)?.title || n.chapterId}</td>
                        <td>{(n.content || '').replace(/<[^>]+>/g, '').slice(0, 80)}...</td>
                        <td>
                          <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete note?')) { await deleteNote(n.id); fetchNotes(); } }}>Delete</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <h5 className="mt-4">Add New Note</h5>
              <Form>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Label>Chapter</Form.Label>
                    <Form.Select value={noteForm.chapterId} onChange={(e) => setNoteForm({ ...noteForm, chapterId: e.target.value })}>
                      <option value="">Select chapter</option>
                      {chaptersChoices.map(c => (
                        <option key={c.id} value={c.id}>{c.title}</option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>Content</Form.Label>
                    <RichTextEditor value={noteForm.content} onChange={(html) => setNoteForm({ ...noteForm, content: html })} />
                  </Col>
                </Row>
                <h6>SEO</h6>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>Meta Description</Form.Label>
                    <Form.Control as="textarea" rows={2} value={noteForm.seoDescription} onChange={(e) => setNoteForm({ ...noteForm, seoDescription: e.target.value })} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>SEO Keywords (comma separated)</Form.Label>
                    <Form.Control value={noteForm.seoKeywords} onChange={(e) => setNoteForm({ ...noteForm, seoKeywords: e.target.value })} />
                  </Col>
                </Row>
                <Button type="button" onClick={async () => {
                  if (!noteForm.chapterId) { alert('Select chapter'); return; }
                  if (notesSaving) return;
                  setNotesSaving(true);
                  try {
                    await createNote(noteForm);
                    setNoteForm({ chapterId: '', content: '', seoDescription: '', seoKeywords: '' });
                    fetchNotes();
                    alert('Note added');
                  } catch (e) {
                    console.error('Add note failed', e);
                    alert('Failed to add note');
                  } finally {
                    setNotesSaving(false);
                  }
                }} disabled={notesSaving}>
                  {notesSaving ? 'Adding...' : 'Add Note'}
                </Button>
              </Form>
            </>
          )}
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Edit Resource</Modal.Title></Modal.Header>
        <Modal.Body>
          {editItem && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Class</Form.Label>
                <Form.Control value={editItem.grade} onChange={(e) => setEditItem({ ...editItem, grade: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Subject</Form.Label>
                <Form.Control value={editItem.subject} onChange={(e) => setEditItem({ ...editItem, subject: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Chapter Title</Form.Label>
                <Form.Control value={editItem.chapterTitle} onChange={(e) => setEditItem({ ...editItem, chapterTitle: e.target.value })} />
              </Form.Group>
              {['notes', 'questions', 'lectures'].map((type) => (
                <div key={type} className="mb-3">
                  <h6 className="text-capitalize">{type}</h6>
                  {editItem[type]?.map((res, idx) => (
                    <Row key={idx} className="mb-2">
                      <Col md={6}>
                        <Form.Control
                          value={res.title}
                          placeholder="Title"
                          onChange={(e) => {
                            const updated = [...editItem[type]];
                            updated[idx].title = e.target.value;
                            setEditItem({ ...editItem, [type]: updated });
                          }}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Control
                          value={res.url}
                          placeholder="URL"
                          onChange={(e) => {
                            const updated = [...editItem[type]];
                            updated[idx].url = e.target.value;
                            setEditItem({ ...editItem, [type]: updated });
                          }}
                        />
                      </Col>
                    </Row>
                  ))}
                </div>
              ))}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleSaveEdit}>Save Changes</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showBlogModal} onHide={() => setShowBlogModal(false)} centered size="lg">
        <Modal.Header closeButton><Modal.Title>Edit Blog</Modal.Title></Modal.Header>
        <Modal.Body>
          {blogEditItem && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control value={blogEditItem.title} onChange={(e) => setBlogEditItem({ ...blogEditItem, title: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Date</Form.Label>
                <Form.Control value={blogEditItem.date} onChange={(e) => setBlogEditItem({ ...blogEditItem, date: e.target.value })} />
              </Form.Group>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Label>Author</Form.Label>
                  <Form.Control value={blogEditItem.author} onChange={(e) => setBlogEditItem({ ...blogEditItem, author: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Tag</Form.Label>
                  <Form.Control value={blogEditItem.tag} onChange={(e) => setBlogEditItem({ ...blogEditItem, tag: e.target.value })} />
                </Col>
              </Row>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select value={blogEditItem.status || 'draft'} onChange={(e) => setBlogEditItem({ ...blogEditItem, status: e.target.value })}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </Form.Select>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Control value={blogEditItem.category || ''} onChange={(e) => setBlogEditItem({ ...blogEditItem, category: e.target.value })} />
                </Col>
              </Row>
              <Form.Check
                type="checkbox"
                className="mb-3"
                label="Featured"
                checked={!!blogEditItem.featured}
                onChange={(e) => setBlogEditItem({ ...blogEditItem, featured: e.target.checked })}
              />
              <Form.Group className="mb-3">
                <Form.Label>Excerpt</Form.Label>
                <Form.Control as="textarea" rows={2} value={blogEditItem.excerpt} onChange={(e) => setBlogEditItem({ ...blogEditItem, excerpt: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Content</Form.Label>
                <RichTextEditor value={blogEditItem.content || ''} onChange={(html) => setBlogEditItem({ ...blogEditItem, content: html })} />
              </Form.Group>
              <hr />
              <h6>SEO</h6>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Label>SEO Title</Form.Label>
                  <Form.Control value={blogEditItem.seoTitle || ''} onChange={(e) => setBlogEditItem({ ...blogEditItem, seoTitle: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Canonical URL</Form.Label>
                  <Form.Control value={blogEditItem.canonicalUrl || ''} onChange={(e) => setBlogEditItem({ ...blogEditItem, canonicalUrl: e.target.value })} />
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Meta Description</Form.Label>
                <Form.Control as="textarea" rows={2} value={blogEditItem.seoDescription || ''} onChange={(e) => setBlogEditItem({ ...blogEditItem, seoDescription: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>SEO Keywords (comma separated)</Form.Label>
                <Form.Control value={(blogEditItem.seoKeywords || []).join(', ')} onChange={(e) => setBlogEditItem({ ...blogEditItem, seoKeywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
              </Form.Group>
              <h6>Social</h6>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Label>Social Title</Form.Label>
                  <Form.Control value={blogEditItem.socialTitle || ''} onChange={(e) => setBlogEditItem({ ...blogEditItem, socialTitle: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Label>Social Image URL</Form.Label>
                  <Form.Control value={blogEditItem.socialImage || ''} onChange={(e) => setBlogEditItem({ ...blogEditItem, socialImage: e.target.value })} />
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Social Description</Form.Label>
                <Form.Control as="textarea" rows={2} value={blogEditItem.socialDescription || ''} onChange={(e) => setBlogEditItem({ ...blogEditItem, socialDescription: e.target.value })} />
              </Form.Group>
              <h6>Advanced</h6>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Label>Featured Image Alt Text</Form.Label>
                  <Form.Control value={blogEditItem.featuredImageAlt || ''} onChange={(e) => setBlogEditItem({ ...blogEditItem, featuredImageAlt: e.target.value })} />
                </Col>
                <Col md={6} className="mb-3 d-flex align-items-end">
                  <Form.Check
                    type="checkbox"
                    label="Allow Comments"
                    checked={blogEditItem.allowComments !== false}
                    onChange={(e) => setBlogEditItem({ ...blogEditItem, allowComments: e.target.checked })}
                  />
                </Col>
              </Row>
              <h6>Related Posts</h6>
              <Row>
                <Col md={12} className="mb-3">
                  <Form.Label>Select related posts</Form.Label>
                  <Select
                    isMulti
                    options={blogChoices
                      .filter(b => (b.slug || b.id) !== (blogEditItem.slug || blogEditItem.id))
                      .map(b => ({ value: b.slug || b.id, label: b.title }))}
                    value={(blogEditItem.related || []).map(v => ({ value: v, label: (blogChoices.find(b => (b.slug || b.id) === v) || {}).title || v }))}
                    onChange={(vals) => setBlogEditItem({ ...blogEditItem, related: (vals || []).map(v => v.value) })}
                    classNamePrefix="react-select"
                    placeholder="Search and select related posts"
                  />
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBlogModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleSaveBlogEdit}>Save</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminPanel;